<!--
	ScrubWords — the argument that assembles as you read. Each word's ink is bound
	to the section's traverse progress (the single scroll engine), so the paragraph
	fills from ghost to full ink as it crosses the viewport — one `--p` write per
	frame on the wrapper; the per-word math is pure CSS (calc + color-mix).

	Truth in motion: the END STATE is the default. `--p` falls back to 1, so no-JS
	and reduced-motion readers get the full-ink paragraph; only an active scroll
	engine ever dims a word.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { scroll } from '$lib/motion/scroll.svelte';
	import { motionDisabled } from '$lib/motion/reduced';

	let {
		text,
		class: klass = ''
	}: {
		text: string;
		class?: string;
	} = $props();

	const words = $derived(text.split(' '));

	let el: HTMLElement;
	let rangeId = $state('');
	const p = $derived(rangeId ? scroll.progress(rangeId) : 1);

	onMount(() => {
		if (motionDisabled()) return; // CSS default (--p: 1) keeps full ink
		const reg = scroll.register(el, { mode: 'traverse' });
		rangeId = reg.id;
		return () => reg.dispose();
	});
</script>

<p bind:this={el} class="scrub-words {klass}" style:--p={rangeId ? p : 1} style:--n={words.length}>
	{#each words as word, i (i)}<span class="scrub-words__w" style:--i={i}>{word + ' '}</span>{/each}
</p>

<style>
	.scrub-words {
		margin: 0;
		/* As a flex item its min-width would resolve to max-content — the whole
		   paragraph on one line, overflowing the page. Allow it to wrap. */
		min-width: 0;
	}
	.scrub-words__w {
		/* Word i fills across 1/n of the traverse, staggered by --i; the +2 spread
		   lets the last words finish before the section leaves. --p defaults to 1
		   (full ink) until the engine drives it. The dim end is ink-400 (4.6:1 on
		   paper): the fill is the flourish, never a contrast violation. */
		--fill: clamp(0, calc(var(--p, 1) * (var(--n) + 2) - var(--i)), 1);
		color: color-mix(in srgb, var(--color-ink-900) calc(var(--fill) * 100%), var(--color-ink-400));
	}
	@media (prefers-reduced-motion: reduce) {
		.scrub-words__w {
			--fill: 1;
		}
	}
</style>
